'use client';

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge } from '@core/ui/primitives/badge';
import { Button } from '@core/ui/primitives/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@core/ui/primitives/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import { Input } from '@core/ui/primitives/input';
import { Spinner } from '@core/ui/primitives/spinner';
import { LuChevronDown, LuTrash2, LuGlobe, LuLock, LuShieldCheck, LuUsers, LuCheck, LuX } from 'react-icons/lu';
import { RadioGroup, RadioGroupItem } from '@core/ui/primitives/radio-group';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useToast } from '@/core/ui/hooks/use-toast';
import type { OrganizationSidebarSelection } from '../OrganizationSidebar';
import { useVaultAccessManagement, type RoleValue } from '@vaults/ui/management/UseVaultAccessManagementHook';
import { RolePrivilegesInfo } from './RolePrivilegesInfo';
import {
  getCachedVaultDetails,
  setCachedVaultDetails,
  hasMembershipCache,
} from '@vaults/ui/management/ClientCacheStore';
import { useVaultDetailsQuery } from '@vaults/ui/management/queries/vaultQueries';

const roleOptions: Array<{ value: RoleValue; label: string }> = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'contributor', label: 'Contributor' },
  { value: 'editor', label: 'Editor' },
  { value: 'manager', label: 'Manager' },
  { value: 'admin', label: 'Admin' },
  { value: 'owner', label: 'Owner' },
];

// Save state interface for parent communication
interface SaveState {
  isDirty: boolean;
  isSaving: boolean;
  canSave: boolean;
  onSave: () => void;
}

export function AccessManagement({
  selection,
  fallbackDetails,
  onReady,
  onSaveStateChange,
}: {
  selection: OrganizationSidebarSelection;
  // Only used for description rendering
  fallbackDetails?: { description?: string };
  onReady?: (opts?: { immediate?: boolean }) => void;
  onSaveStateChange?: (state: SaveState | null) => void;
}) {
  const { toast } = useToast();
  const vaultId = selection.vaultId ?? null;
  const organizationId = selection.organizationId ?? null;

  const {
    loading,
    error,
    members,
    invites,
    memberCount,
    inviteMember,
    updateMemberRole,
    removeMembershipEntry,
    allowlist,
    organizationAllowlist,
    addAllowlistDomain,
    removeAllowlistEntry,
  } = useVaultAccessManagement({ vaultId, organizationId });

  // Visibility state with persistence
  const [visibility, setVisibility] = useState<'private' | 'protected' | 'org' | 'public'>('private');
  const [initialVisibility, setInitialVisibility] = useState<'private' | 'protected' | 'org' | 'public'>('private');
  const [savingVisibility, setSavingVisibility] = useState(false);
  const vaultDetailsQuery = useVaultDetailsQuery(vaultId);
  const [loadingVaultData, setLoadingVaultData] = useState(true);

  const visibilityChanged = visibility !== initialVisibility;

  // Seed from cache and then fold in query results
  useEffect(() => {
    if (!vaultId) return;
    setLoadingVaultData(true);
    const cached = getCachedVaultDetails(vaultId);
    if (cached?.visibility) {
      const vis = cached.visibility;
      setVisibility(vis);
      setInitialVisibility(vis);
      setLoadingVaultData(false);
    }
  }, [vaultId]);

  useEffect(() => {
    if (!vaultId) return;
    if (vaultDetailsQuery.isError) {
      if (!getCachedVaultDetails(vaultId)) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: (vaultDetailsQuery.error as Error)?.message || 'Failed to load vault data',
        });
      }
      setLoadingVaultData(false);
      return;
    }
    if (vaultDetailsQuery.data?.vault) {
      const data = vaultDetailsQuery.data;
      const rawVisibility = (data.vault?.visibility || 'private') as string;
      const allowedVisibilities: Array<'private' | 'protected' | 'org' | 'public'> = [
        'private',
        'protected',
        'org',
        'public',
      ];
      const vaultVisibility = allowedVisibilities.includes(rawVisibility as any)
        ? (rawVisibility as 'private' | 'protected' | 'org' | 'public')
        : 'private';
      setVisibility(vaultVisibility);
      setInitialVisibility(vaultVisibility);
      setCachedVaultDetails(vaultId, {
        visibility: vaultVisibility,
        slug: data?.vault?.slug,
        organizationUrlSlug: data?.vault?.organizationUrlSlug,
        repoUrl: data?.vault?.repoUrl ?? null,
        mode: data?.vault?.mode ?? null,
        detectedSourceWiki: data?.vault?.detectedSourceWiki ?? null,
      });
      setLoadingVaultData(false);
    } else if (vaultDetailsQuery.isPending || vaultDetailsQuery.isFetching) {
      setLoadingVaultData(true);
    }
  }, [
    vaultId,
    vaultDetailsQuery.isPending,
    vaultDetailsQuery.isFetching,
    vaultDetailsQuery.isError,
    vaultDetailsQuery.data,
    vaultDetailsQuery.error,
  ]);

  // Signal readiness when all initial data (vault details + members) finishes loading
  useEffect(() => {
    const cacheReady = hasMembershipCache(vaultId, organizationId);
    const hasVaultCached = Boolean(getCachedVaultDetails(vaultId));
    if (!loadingVaultData && (!loading || cacheReady)) {
      onReady?.({ immediate: hasVaultCached && cacheReady });
    }
  }, [loadingVaultData, loading, onReady, vaultId, organizationId]);

  const [inviteForm, setInviteForm] = useState<{
    email: string;
    role: RoleValue;
  }>(() => ({
    email: '',
    role: 'contributor',
  }));
  const [domainInput, setDomainInput] = useState('');
  const [pendingEntryId, setPendingEntryId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [addingDomain, setAddingDomain] = useState(false);
  const [pendingDomainId, setPendingDomainId] = useState<string | null>(null);

  const combinedEntries = useMemo(() => {
    const rows = [...members, ...invites];
    return rows.sort((a, b) => {
      if (a.type !== b.type) return a.type === 'membership' ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
  }, [members, invites]);

  const stats = useMemo(
    () => [
      { label: 'Members', value: memberCount, hint: 'People with direct access' },
      { label: 'Pending invites', value: invites.length, hint: 'Awaiting acceptance' },
      {
        label: 'Allowlisted domains',
        value: allowlist.length + organizationAllowlist.length,
        hint: 'Vault + org coverage',
      },
    ],
    [memberCount, invites.length, allowlist.length, organizationAllowlist.length]
  );

  const handleInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const email = inviteForm.email.trim();
    if (!email) {
      toast({ variant: 'destructive', title: 'Error', description: 'Email is required.' });
      return;
    }
    setCreatingInvite(true);
    try {
      await inviteMember({ email, role: inviteForm.role });
      setInviteForm({ email: '', role: 'contributor' });
      toast({ variant: 'success', title: 'Success', description: `Invite sent to ${email}.` });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to send invite right now.',
      });
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleRoleChange = async (entryId: string, role: RoleValue) => {
    setPendingEntryId(entryId);
    try {
      await updateMemberRole({ entryId, role });
      toast({ variant: 'success', title: 'Success', description: 'Role updated.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to update role.',
      });
    } finally {
      setPendingEntryId(null);
    }
  };

  const handleRemoveEntry = async (entryId: string) => {
    setPendingEntryId(entryId);
    try {
      await removeMembershipEntry(entryId);
      toast({ variant: 'success', title: 'Success', description: 'Entry removed.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to remove entry.',
      });
    } finally {
      setPendingEntryId(null);
      setConfirmRemoveId(null);
    }
  };

  const handleAddDomain = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalized = domainInput.trim().replace(/^@+/, '').toLowerCase();
    if (!normalized) {
      toast({ variant: 'destructive', title: 'Error', description: 'Domain is required.' });
      return;
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(normalized)) {
      toast({ variant: 'destructive', title: 'Error', description: 'Enter a valid domain.' });
      return;
    }
    setAddingDomain(true);
    try {
      await addAllowlistDomain({ domain: normalized, scope: 'VAULT' });
      setDomainInput('');
      toast({ variant: 'success', title: 'Success', description: `Added ${normalized} to the allowlist.` });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to add domain.',
      });
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (entryId: string) => {
    setPendingDomainId(entryId);
    try {
      await removeAllowlistEntry(entryId);
      toast({ variant: 'success', title: 'Success', description: 'Domain removed.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to remove domain.',
      });
    } finally {
      setPendingDomainId(null);
    }
  };

  const handleSaveVisibility = useCallback(async () => {
    if (!vaultId || !visibilityChanged) return;

    setSavingVisibility(true);
    try {
      const response = await fetch(`/api/vaults/${vaultId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update visibility' }));
        throw new Error(errorData.error || 'Failed to update visibility');
      }

      setInitialVisibility(visibility);
      // Update cache to keep tabs in sync without refetch
      setCachedVaultDetails(vaultId, { visibility });
      toast({ variant: 'success', title: 'Success', description: 'Visibility updated successfully.' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Unable to update visibility.',
      });
    } finally {
      setSavingVisibility(false);
    }
  }, [vaultId, visibility, visibilityChanged, toast]);

  // Communicate save state to parent for header action bar
  useEffect(() => {
    if (!vaultId) {
      onSaveStateChange?.(null);
      return;
    }
    onSaveStateChange?.({
      isDirty: visibilityChanged,
      isSaving: savingVisibility,
      canSave: visibilityChanged && !savingVisibility,
      onSave: handleSaveVisibility,
    });
  }, [vaultId, visibilityChanged, savingVisibility, handleSaveVisibility, onSaveStateChange]);

  // Cleanup save state on unmount
  useEffect(() => {
    return () => {
      onSaveStateChange?.(null);
    };
  }, [onSaveStateChange]);

  if (!vaultId) {
    return (
      <section className='rounded-xl border border-dashed p-6 text-sm text-muted-foreground'>
        Select a vault from the sidebar to manage access, invitations, and domain allowlists.
      </section>
    );
  }

  if (loadingVaultData) {
    return (
      <div className='flex h-full items-center justify-center'>
        <Badge variant='outline' className='gap-2'>
          <Spinner />
          Loading vault settings
        </Badge>
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 space-y-4 overflow-y-auto my-1 scrollbar-fade'>
        <Card>
          <CardHeader>
            <CardTitle className='text-sm'>Visibility</CardTitle>
            <CardDescription>Control who can access this vault.</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={visibility}
              onValueChange={(val) => setVisibility(val as any)}
              className='grid grid-cols-2 gap-3'>
              {[
                {
                  value: 'private',
                  label: 'Private',
                  hint: 'Only invited members can see the vault.',
                  icon: LuLock,
                },
                {
                  value: 'protected',
                  label: 'Protected',
                  hint: 'Visible to any signed-in user.',
                  icon: LuShieldCheck,
                },
                { value: 'org', label: 'Organization', hint: 'Anyone in the org can read.', icon: LuUsers },
                { value: 'public', label: 'Public', hint: 'Visible to anyone with the link.', icon: LuGlobe },
              ].map((option) => (
                <label
                  key={option.value}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm transition hover:border-brand/50',
                    visibility === option.value && 'border-brand bg-brand/10'
                  )}>
                  <RadioGroupItem
                    value={option.value as any}
                    className='mt-0.5 [&_svg]:!fill-brand data-[state=checked]:border-brand/60 focus-visible:!ring-0 focus-visible:!border-brand'
                  />
                  <span className='flex flex-1 flex-col gap-1'>
                    <span className='flex items-center gap-2 font-medium text-foreground'>
                      <option.icon className='h-4 w-4 opacity-80' />
                      {option.label}
                    </span>
                    <span className='text-xs text-muted-foreground'>{option.hint}</span>
                  </span>
                </label>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Omit legacy description block to keep UI concise */}

        {visibility !== 'public' && (
          <Card>
            <CardHeader className='space-y-3'>
              <div className='flex flex-row flex-wrap items-center justify-between gap-3'>
                <div className='flex flex-col gap-1.5'>
                  <CardTitle className='text-sm'>Members &amp; roles</CardTitle>
                  <CardDescription>Invite collaborators and manage their permissions.</CardDescription>
                </div>
                <form onSubmit={handleInvite} className='flex flex-wrap items-center gap-2'>
                  <Input
                    type='email'
                    placeholder='name@example.com'
                    value={inviteForm.email}
                    onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
                    className='w-48'
                    required
                  />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='outline'>
                        {roleOptions.find((o) => o.value === inviteForm.role)?.label ?? 'Select role'}
                        <LuChevronDown className='h-3 w-3 opacity-50' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuRadioGroup
                        value={inviteForm.role}
                        onValueChange={(value) => setInviteForm((prev) => ({ ...prev, role: value as RoleValue }))}>
                        {roleOptions
                          .filter((option) => option.value !== 'owner')
                          .map((option) => (
                            <DropdownMenuRadioItem key={option.value} value={option.value}>
                              {option.label}
                            </DropdownMenuRadioItem>
                          ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button type='submit' disabled={creatingInvite}>
                    {creatingInvite ? 'Sending…' : 'Invite'}
                  </Button>
                </form>
              </div>
              <RolePrivilegesInfo role={inviteForm.role} className='w-full rounded-md border border-dashed p-2' />
            </CardHeader>
            <CardContent>
              {error && !loading ? (
                <p className='text-sm text-rose-600'>{error}</p>
              ) : loading && combinedEntries.length === 0 ? (
                <p className='text-sm text-muted-foreground'>Loading members…</p>
              ) : combinedEntries.length === 0 ? (
                <p className='text-sm text-muted-foreground'>
                  No members or invites yet. Use the form above to add collaborators.
                </p>
              ) : (
                <div className='space-y-2'>
                  {combinedEntries.map((entry) => {
                    const isOwner = entry.role === 'owner';
                    return (
                      <div
                        key={entry.id}
                        className='flex items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2'>
                        <div className='flex min-w-0 flex-1 flex-col'>
                          <span className='truncate text-sm'>{entry.email}</span>
                          {entry.user?.name && (
                            <span className='truncate text-xs text-muted-foreground'>{entry.user.name}</span>
                          )}
                        </div>
                        <div className='flex items-center gap-2'>
                          {entry.type === 'invite' && (
                            <Badge variant='warning'>
                              Pending
                            </Badge>
                          )}
                          {isOwner ? (
                            <Badge variant='outline' className='font-normal'>
                              Owner
                            </Badge>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant='outline' className='h-9' disabled={pendingEntryId === entry.id}>
                                  {roleOptions.find((o) => o.value === entry.role)?.label ?? entry.role}
                                  <LuChevronDown className='h-3 w-3 opacity-50' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end'>
                                <DropdownMenuRadioGroup
                                  value={entry.role}
                                  onValueChange={(value) => handleRoleChange(entry.id, value as RoleValue)}>
                                  {roleOptions
                                    .filter((option) => option.value !== 'owner')
                                    .map((option) => (
                                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                                        {option.label}
                                      </DropdownMenuRadioItem>
                                    ))}
                                </DropdownMenuRadioGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {!isOwner && (
                            <div className='flex h-8 w-8 items-center justify-center'>
                              {confirmRemoveId === entry.id ? (
                                <div className='flex items-center gap-1'>
                                  <button
                                    type='button'
                                    onClick={() => handleRemoveEntry(entry.id)}
                                    disabled={pendingEntryId === entry.id}
                                    className='flex h-6 w-6 items-center justify-center rounded text-brand hover:bg-brand/10 disabled:opacity-50'
                                    title='Confirm remove'
                                    aria-label='Confirm remove'>
                                    <LuCheck className='h-3.5 w-3.5' />
                                  </button>
                                  <button
                                    type='button'
                                    onClick={() => setConfirmRemoveId(null)}
                                    className='flex h-6 w-6 items-center justify-center rounded text-destructive hover:bg-destructive/10'
                                    title='Cancel'
                                    aria-label='Cancel'>
                                    <LuX className='h-3.5 w-3.5' />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type='button'
                                  onClick={() => setConfirmRemoveId(entry.id)}
                                  disabled={pendingEntryId === entry.id}
                                  className='flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:text-destructive disabled:opacity-50'>
                                  <LuTrash2 className='h-4 w-4' />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {visibility !== 'public' && (
          <Card>
            <CardHeader className='flex-row flex-wrap items-center justify-between gap-3'>
              <div className='flex flex-col gap-1.5'>
                <CardTitle className='text-sm'>Domain allowlist</CardTitle>
                <CardDescription>Only emails from these domains can view draft content.</CardDescription>
              </div>
              <form onSubmit={handleAddDomain} className='flex flex-wrap items-center gap-2'>
                <Input
                  type='text'
                  placeholder='example.com'
                  value={domainInput}
                  onChange={(event) => setDomainInput(event.target.value)}
                  className='w-48'
                />
                <Button type='submit' disabled={addingDomain}>
                  {addingDomain ? 'Adding…' : 'Add domain'}
                </Button>
              </form>
            </CardHeader>
            <CardContent>
              {allowlist.length === 0 ? (
                <p className='text-sm text-muted-foreground'>
                  No vault-specific domains yet. Add one above to restrict access.
                </p>
              ) : (
                <ul className='space-y-2 text-sm'>
                  {allowlist.map((entry) => {
                    const display = entry.value.startsWith('@') ? entry.value : `@${entry.value}`;
                    return (
                      <li
                        key={entry.id}
                        className='flex items-center justify-between rounded-lg border border-dashed px-3 py-2'>
                        <span>{display}</span>
                        <Button
                          size='icon'
                          variant='ghost'
                          className='hover:text-destructive'
                          disabled={pendingDomainId === entry.id}
                          onClick={() => handleRemoveDomain(entry.id)}>
                          <LuTrash2 className='h-4 w-4' />
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {organizationAllowlist.length > 0 && (
                <div className='mt-4 rounded-lg border border-dashed bg-muted p-3 text-xs text-muted-foreground'>
                  <p className='mb-2 font-medium text-foreground'>Organization domains</p>
                  <ul className='flex flex-wrap gap-2'>
                    {organizationAllowlist.map((entry) => {
                      const display = entry.value.startsWith('@') ? entry.value : `@${entry.value}`;
                      return (
                        <li key={entry.id} className='rounded-full border px-3 py-1'>
                          {display}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default AccessManagement;
